// Ocean Insight Pro Mobile - Location Picker Screen
// Interactive map to select a location and view its conditions

import React, { useState, useRef, useEffect } from 'react';
import { View, StyleSheet, Alert, ScrollView, Platform, TouchableOpacity } from 'react-native';
import {
  Card,
  Title,
  Paragraph,
  Button,
  useTheme,
  ActivityIndicator,
  FAB,
  Portal,
  Modal,
} from 'react-native-paper';
import { PlatformMap } from '../components/PlatformMap';
import { MarineForecastBrief } from '../components/MarineForecastBrief';
import { getFishingDayForecasts } from '../utils/forecastHours';
import { getAverageSeaState, getSeaState } from '../utils/seaState';
import { formatWaveHeight, formatWavePeriod, hasMarineData, marineCoverageDays } from '../utils/marineData';
import { MaterialIcons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import type { StackNavigationProp } from '@react-navigation/stack';

import { RootState, AppDispatch } from '../store';
import { fetchCurrentWeather, fetchHourlyWeatherForecast, fetchWeatherForecast } from '../store/weatherSlice';
import type { RootStackParamList, GeoCoordinate, WeatherForecast } from '../types';
import { locationService } from '../services/locationService';

type NavigationProp = StackNavigationProp<RootStackParamList>;

export default function LocationPickerScreen() {
  const theme = useTheme();
  const navigation = useNavigation<NavigationProp>();
  const dispatch = useDispatch<AppDispatch>();

  const [selectedLocation, setSelectedLocation] = useState<GeoCoordinate | null>(null);
  const [showWeatherModal, setShowWeatherModal] = useState(false);
  const [isLoadingWeather, setIsLoadingWeather] = useState(false);
  const [locationWeather, setLocationWeather] = useState<any>(null);
  const [locationForecast, setLocationForecast] = useState<WeatherForecast[]>([]);
  const [locationHourlyForecast, setLocationHourlyForecast] = useState<WeatherForecast[]>([]);
  const [selectedDayForecast, setSelectedDayForecast] = useState<WeatherForecast | null>(null);
  const [showDayDetailModal, setShowDayDetailModal] = useState(false);

  const { locationPreferences } = useSelector((state: RootState) => state.user);

  // Initial center and zoom - use Gulf of Mexico as default
  const [mapCenter, setMapCenter] = useState(() => {
    if (selectedLocation) {
      return { lat: selectedLocation.latitude, lng: selectedLocation.longitude };
    } else if (locationPreferences.savedLocation) {
      return { lat: locationPreferences.savedLocation.coordinates.latitude, lng: locationPreferences.savedLocation.coordinates.longitude };
    } else {
      return { lat: 25.0, lng: -90.0 }; // Gulf of Mexico default
    }
  });
  const [zoom, setZoom] = useState(8);

  // Center map on user location when component mounts
  useEffect(() => {
    locationService.getCurrentLocation()
      .then(location => {
        if (location) {
          setMapCenter({ lat: location.latitude, lng: location.longitude });
          setZoom(10); // Zoom in closer for user location
        }
      })
      .catch(error => {
        console.log('Could not get user location, using default center');
      });
  }, []);
  
  // Original region calculation for reference
  const initialRegion = selectedLocation
    ? {
        latitude: selectedLocation.latitude,
        longitude: selectedLocation.longitude,
        latitudeDelta: 0.5,
        longitudeDelta: 0.5,
      }
    : locationPreferences.savedLocation
    ? {
        latitude: locationPreferences.savedLocation.coordinates.latitude,
        longitude: locationPreferences.savedLocation.coordinates.longitude,
        latitudeDelta: 0.5,
        longitudeDelta: 0.5,
      }
    : {
        latitude: 25.0,
        longitude: -90.0,
        latitudeDelta: 12.0,
        longitudeDelta: 15.0,
      };

  const handleMapClick = (lat: number, lng: number) => {
    setSelectedLocation({
      latitude: lat,
      longitude: lng,
    });
  };
  
  // Prepare markers for Leaflet
  const mapMarkers = selectedLocation ? [{
    position: { lat: selectedLocation.latitude, lng: selectedLocation.longitude },
    icon: '📍',
    size: [32, 32] as [number, number],
    title: 'Selected Location'
  }] : [];

  const handleCheckConditions = async () => {
    if (!selectedLocation) {
      Alert.alert('No Location Selected', 'Please tap on the map to select a location first.');
      return;
    }

    setIsLoadingWeather(true);
    setShowWeatherModal(true);

    try {
      // Keep the overview compact while retaining the full hourly planning data.
      const [weatherData, forecastData, hourlyData] = await Promise.all([
        dispatch(fetchCurrentWeather(selectedLocation)).unwrap(),
        dispatch(fetchWeatherForecast(selectedLocation)).unwrap(),
        dispatch(fetchHourlyWeatherForecast(selectedLocation)).unwrap(),
      ]);
      setLocationWeather(weatherData);
      setLocationForecast(forecastData);
      setLocationHourlyForecast(hourlyData);
    } catch (error) {
      Alert.alert('Error', 'Failed to fetch weather data for this location.');
      setShowWeatherModal(false);
    } finally {
      setIsLoadingWeather(false);
    }
  };

  const handleViewDetails = () => {
    if (selectedLocation) {
      setShowWeatherModal(false);
      navigation.navigate('WeatherDetails', { location: selectedLocation });
    }
  };

  const getWindDirectionIcon = (degrees: number) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    const index = Math.round(((degrees %= 360) < 0 ? degrees + 360 : degrees) / 45) % 8;
    return directions[index];
  };

  const getWeatherIcon = (conditions: string) => {
    if (conditions.includes('Clear')) return 'wb-sunny';
    if (conditions.includes('Cloudy')) return 'cloud';
    if (conditions.includes('Rain')) return 'grain';
    if (conditions.includes('Thunderstorm')) return 'flash-on';
    if (conditions.includes('Fog')) return 'blur-on';
    if (conditions.includes('Snow')) return 'ac-unit';
    return 'wb-cloudy';
  };

  return (
    <View style={styles.container}>
      <PlatformMap
        center={mapCenter}
        zoom={zoom}
        markers={mapMarkers}
        overlays={[]}
        shapes={[]}
        baseMapType="ocean"
        onMapClick={handleMapClick}
      />

      {/* Instructions Card */}
      <Card style={styles.instructionCard}>
        <Card.Content style={styles.instructionContent}>
          <MaterialIcons name="touch-app" size={24} color={theme.colors.primary} />
          <View style={styles.instructionText}>
            <Paragraph style={styles.instructionTitle}>Tap anywhere on the map</Paragraph>
            <Paragraph style={styles.instructionSubtitle}>
              {selectedLocation
                ? `Selected: ${selectedLocation.latitude.toFixed(2)}°, ${selectedLocation.longitude.toFixed(2)}°`
                : 'Select a location to check conditions'}
            </Paragraph>
          </View>
        </Card.Content>
      </Card>

      {/* Check Conditions FAB */}
      <FAB
        style={[styles.fab, { backgroundColor: theme.colors.primary }]}
        icon="weather-partly-cloudy"
        label="Check Conditions"
        onPress={handleCheckConditions}
        visible={!!selectedLocation}
      />

      {/* Weather Modal */}
      <Portal>
        <Modal
          visible={showWeatherModal}
          onDismiss={() => setShowWeatherModal(false)}
          contentContainerStyle={[styles.modalContent, { backgroundColor: theme.colors.surface }]}
        >
          {isLoadingWeather ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" />
              <Paragraph style={{ marginTop: 16 }}>Loading weather data...</Paragraph>
            </View>
          ) : locationWeather ? (
            <ScrollView 
              showsVerticalScrollIndicator={false} 
              style={styles.modalScrollView}
              contentContainerStyle={{ padding: 24 }}
            >
              <View style={styles.modalHeader}>
                <Title style={styles.modalTitle}>Current Conditions</Title>
                <Paragraph style={styles.locationText}>
                  {selectedLocation?.latitude.toFixed(2)}°, {selectedLocation?.longitude.toFixed(2)}°
                </Paragraph>
              </View>
              
              <View style={styles.weatherSummary}>
                <View style={styles.weatherIconContainer}>
                  <MaterialIcons
                    name={locationWeather.weather.conditions.includes('Clear') ? 'wb-sunny' :
                          locationWeather.weather.conditions.includes('Cloud') ? 'cloud' :
                          locationWeather.weather.conditions.includes('Rain') ? 'grain' :
                          locationWeather.weather.conditions.includes('Thunder') ? 'flash-on' :
                          'wb-cloudy'}
                    size={56}
                    color={theme.colors.primary}
                  />
                </View>
                <View style={styles.mainWeather}>
                  <Title style={styles.temperatureText}>{locationWeather.weather.temperature}°F</Title>
                  <Paragraph style={styles.conditionsText}>{locationWeather.weather.conditions}</Paragraph>
                </View>
              </View>

              <View style={styles.detailsContainer}>
                <View style={styles.detailRow}>
                  <MaterialCommunityIcons name="weather-windy" size={24} color={theme.colors.primary} />
                  <View style={styles.detailText}>
                    <Paragraph style={styles.periodDetailLabel}>Wind</Paragraph>
                    <Paragraph style={styles.periodDetailValue}>
                      {locationWeather.wind.speed} kt from {getWindDirectionIcon(locationWeather.wind.direction)}
                    </Paragraph>
                  </View>
                </View>

                <View style={styles.detailRow}>
                  <MaterialCommunityIcons name="waves" size={24} color={theme.colors.primary} />
                  <View style={styles.detailText}>
                    <Paragraph style={styles.periodDetailLabel}>Waves</Paragraph>
                    <Paragraph style={styles.periodDetailValue}>
                      {hasMarineData(locationWeather.waves)
                        ? `${locationWeather.waves.height} ft @ ${locationWeather.waves.period}s`
                        : 'Marine data unavailable'}
                    </Paragraph>
                  </View>
                </View>

                <View style={styles.detailRow}>
                  <MaterialCommunityIcons name="gauge" size={24} color={theme.colors.primary} />
                  <View style={styles.detailText}>
                    <Paragraph style={styles.periodDetailLabel}>Pressure</Paragraph>
                    <Paragraph style={styles.periodDetailValue}>{locationWeather.weather.pressure.toFixed(2)} inHg</Paragraph>
                  </View>
                </View>

                <View style={styles.detailRow}>
                  <MaterialIcons name="visibility" size={24} color={theme.colors.primary} />
                  <View style={styles.detailText}>
                    <Paragraph style={styles.periodDetailLabel}>Visibility</Paragraph>
                    <Paragraph style={styles.periodDetailValue}>{locationWeather.weather.visibility} nm</Paragraph>
                  </View>
                </View>
              </View>

              {/* Marine Forecast */}
              {locationForecast.length > 0 && (
                <View style={styles.forecastSection}>
                  <Title style={styles.forecastTitle}>{locationForecast.length}-Day Wind · {marineCoverageDays(locationForecast)}-Day Marine Forecast</Title>
                  <ScrollView 
                    horizontal
                    showsHorizontalScrollIndicator={true}
                    style={styles.forecastScrollView}
                    contentContainerStyle={styles.forecastContainer}
                  >
                    {locationForecast.map((day, index) => {
                      const date = new Date(day.timestamp);
                      const dayHours = locationHourlyForecast.filter(hour => hour.timestamp.slice(0, 10) === day.timestamp.slice(0, 10));
                      const fishingDayHours = getFishingDayForecasts(dayHours);
                      const seaState = fishingDayHours.length ? getAverageSeaState(fishingDayHours) : getSeaState(day.waves, day.wind);
                      const dayName = index === 0 
                        ? 'Today' 
                        : date.toLocaleDateString('en-US', { weekday: 'short' });
                      const dateLabel = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      
                      return (
                        <TouchableOpacity
                          key={day.id}
                          style={styles.forecastDay}
                          onPress={() => {
                            setSelectedDayForecast(day);
                            setShowDayDetailModal(true);
                          }}
                        >
                          <Paragraph style={styles.dayName}>{dayName}</Paragraph>
                          <Paragraph style={styles.dayDate}>{dateLabel}</Paragraph>
                          <MaterialIcons
                            name={getWeatherIcon(day.weather.conditions)}
                            size={20}
                            color={theme.colors.primary}
                          />
                          <Paragraph style={styles.forecastTemp}>
                            {Math.round(day.weather.temperature)}°
                          </Paragraph>
                          <View style={[styles.seaStateBadge, { backgroundColor: seaState.backgroundColor }]}>
                            <MaterialIcons name="waves" size={11} color={seaState.color} />
                            <Paragraph style={[styles.seaStateLabel, { color: seaState.color }]}>{seaState.label}</Paragraph>
                          </View>
                          <View style={styles.forecastInfo}>
                            <View style={styles.forecastDetails}>
                              <MaterialIcons name="air" size={10} color={theme.colors.onSurfaceVariant} />
                              <Paragraph style={styles.forecastValue}>
                                {day.wind.minSpeed && day.wind.maxSpeed 
                                  ? `${day.wind.minSpeed}-${day.wind.maxSpeed}kt`
                                  : `${day.wind.speed}kt`}
                              </Paragraph>
                            </View>
                            <View style={styles.forecastDetails}>
                              <MaterialIcons name="waves" size={10} color={theme.colors.onSurfaceVariant} />
                              <Paragraph style={styles.forecastValue}>
                                {hasMarineData(day.waves) && day.waves.minHeight !== undefined && day.waves.maxHeight !== undefined
                                  ? `${day.waves.minHeight}-${day.waves.maxHeight}ft`
                                  : formatWaveHeight(day.waves, false)}
                              </Paragraph>
                            </View>
                            <View style={styles.forecastDetails}>
                              <MaterialCommunityIcons name="timer-outline" size={10} color={theme.colors.onSurfaceVariant} />
                              <Paragraph style={styles.forecastValue}>
                                {hasMarineData(day.waves) && day.waves.minPeriod !== undefined && day.waves.maxPeriod !== undefined
                                  ? `${day.waves.minPeriod}-${day.waves.maxPeriod}s`
                                  : formatWavePeriod(day.waves, false)}
                              </Paragraph>
                            </View>
                          </View>
                        </TouchableOpacity>
                      );
                    })}
                  </ScrollView>
                </View>
              )}

              <View style={styles.modalButtons}>
                <Button
                  mode="outlined"
                  onPress={() => setShowWeatherModal(false)}
                  style={styles.modalButton}
                >
                  Close
                </Button>
                <Button
                  mode="contained"
                  onPress={handleViewDetails}
                  style={styles.modalButton}
                >
                  View Details
                </Button>
              </View>
            </ScrollView>
          ) : null}
        </Modal>
        
        {/* Day Detail Modal */}
        <Modal
          visible={showDayDetailModal}
          onDismiss={() => setShowDayDetailModal(false)}
          contentContainerStyle={[styles.dayDetailModalContent, { backgroundColor: theme.colors.surface }]}
        >
          {selectedDayForecast && (
            <ScrollView 
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ 
                paddingHorizontal: 24,
                paddingTop: 24,
                paddingBottom: 32,
                flexGrow: 1 
              }}
            >
              <View style={styles.dayModalHeader}>
                <Title>
                  {new Date(selectedDayForecast.timestamp).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </Title>
                <MaterialIcons
                  name={getWeatherIcon(selectedDayForecast.weather.conditions)}
                  size={32}
                  color={theme.colors.primary}
                />
              </View>
              
              <Paragraph style={styles.dayConditionsText}>
                {selectedDayForecast.weather.conditions}
              </Paragraph>

              <MarineForecastBrief
                dailyForecast={locationForecast}
                hourlyForecast={locationHourlyForecast}
                initialDate={selectedDayForecast.timestamp.slice(0, 10)}
                showHeader={false}
                showDateSelector={false}
              />
              
              <Button
                mode="outlined"
                onPress={() => setShowDayDetailModal(false)}
                style={{ marginTop: 24 }}
              >
                Close
              </Button>
            </ScrollView>
          )}
        </Modal>
      </Portal>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  map: {
    flex: 1,
  },
  instructionCard: {
    position: 'absolute',
    top: 16,
    left: 16,
    right: 16,
    elevation: 4,
  },
  instructionContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  instructionText: {
    marginLeft: 12,
    flex: 1,
  },
  instructionTitle: {
    fontWeight: 'bold',
    fontSize: 14,
  },
  instructionSubtitle: {
    fontSize: 12,
    opacity: 0.7,
  },
  fab: {
    position: 'absolute',
    right: 16,
    bottom: 16,
  },
  modalContent: {
    marginHorizontal: 20,
    marginVertical: 40,
    borderRadius: 12,
    maxHeight: '90%',
    overflow: 'hidden',
  },
  modalScrollView: {
    flexGrow: 1,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    fontSize: 20,
    marginBottom: 4,
  },
  locationText: {
    fontSize: 16,
    opacity: 0.7,
  },
  weatherSummary: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    paddingVertical: 8,
  },
  weatherIconContainer: {
    marginRight: 16,
  },
  mainWeather: {
    alignItems: 'flex-start',
  },
  temperatureText: {
    fontSize: 32,
    lineHeight: 36,
    marginBottom: 2,
  },
  conditionsText: {
    fontSize: 16,
    opacity: 0.8,
  },
  detailsContainer: {
    marginBottom: 24,
  },
  detailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
  },
  detailText: {
    marginLeft: 16,
    flex: 1,
  },
  detailLabel: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 2,
  },
  detailValue: {
    fontSize: 14,
    opacity: 0.7,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 20,
    marginBottom: 8,
  },
  modalButton: {
    flex: 0.48,
  },
  forecastSection: {
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
  },
  forecastTitle: {
    fontSize: 17,
    marginBottom: 12,
    textAlign: 'center',
    fontWeight: '500',
  },
  forecastScrollView: {
    maxHeight: 200,
  },
  forecastContainer: {
    flexDirection: 'row',
    paddingRight: 8,
  },
  forecastDay: {
    width: 112,
    minHeight: 196,
    alignItems: 'center',
    padding: 8,
    marginRight: 8,
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: 10,
  },
  dayName: {
    fontSize: 11,
    fontWeight: '600',
  },
  dayDate: {
    fontSize: 9,
    color: '#6B7280',
    marginBottom: 6,
  },
  forecastTemp: {
    fontSize: 14,
    fontWeight: 'bold',
    marginTop: 3,
    marginBottom: 5,
  },
  seaStateBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'stretch',
    justifyContent: 'center',
    minHeight: 30,
    paddingVertical: 3,
    paddingHorizontal: 4,
    borderRadius: 6,
    marginBottom: 6,
  },
  seaStateLabel: {
    fontSize: 9,
    lineHeight: 12,
    fontWeight: '700',
    marginLeft: 3,
    textAlign: 'center',
  },
  forecastInfo: {
    alignItems: 'center',
  },
  forecastDetails: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 2,
  },
  forecastValue: {
    fontSize: 10,
    marginLeft: 2,
  },
  dayDetailModalContent: {
    margin: 20,
    marginBottom: 40,
    padding: 0,
    borderRadius: 12,
    maxHeight: '90%',
    overflow: 'hidden',
  },
  dayModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  dayConditionsText: {
    fontSize: 16,
    textAlign: 'center',
    opacity: 0.8,
  },
  periodSection: {
    marginBottom: 16,
  },
  periodHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  periodTitle: {
    fontSize: 18,
    marginLeft: 8,
  },
  periodDetails: {
    paddingLeft: 32,
  },
  periodDetailRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  periodDetailLabel: {
    marginLeft: 8,
    marginRight: 8,
    fontWeight: '500',
  },
  periodDetailValue: {
    flex: 1,
  },
  additionalDetails: {
    paddingHorizontal: 16,
  },
});
